/**
 * Type-safe central Event Bus for communication across modules.
 * Decouples WebSocket messages, Supabase broadcasts, and local page reactions.
 */

export type RealtimeEventMap = {
  // App Mobile Events
  'app:notification': { id: string; title: string; message: string; type: string };
  'app:streak_updated': { userId: string; streak: number };
  'app:points_awarded': { userId: string; points: number; total: number };

  // Manager Panel Events
  'manager:new_discard': { discardId: string; userId: string; ecopontoId: string; weight: number };
  'manager:alert': { type: 'error' | 'warning' | 'info'; title: string; message: string; machineId?: string };
  'manager:stats_refetch': { location?: string };

  // Shared Remote Device/IoT Telemetry Events
  'device:heartbeat': { machineId: string; battery: number; signal: number; timestamp: string };
  'device:telemetry': { machineId: string; fillLevel: number; temperature: number; status: 'online' | 'maintenance' | 'critical' };
  'device:status_change': { machineId: string; status: string; previousStatus: string };
};

export type EventCallback<T> = (data: T) => void;

class EventBus {
  private listeners = new Map<keyof RealtimeEventMap, Set<EventCallback<any>>>();

  /**
   * Subscribe to a specific real-time or local event.
   * Returns an unsubscribe function to prevent memory leaks automatically.
   */
  on<E extends keyof RealtimeEventMap>(event: E, callback: EventCallback<RealtimeEventMap[E]>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Return auto-unsubscriber
    return () => {
      this.off(event, callback);
    };
  }

  /**
   * Unsubscribe a listener from an event.
   */
  off<E extends keyof RealtimeEventMap>(event: E, callback: EventCallback<RealtimeEventMap[E]>): void {
    const list = this.listeners.get(event);
    if (list) {
      list.delete(callback);
      if (list.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Publish an event to all active subscribers.
   */
  emit<E extends keyof RealtimeEventMap>(event: E, data: RealtimeEventMap[E]): void {
    const list = this.listeners.get(event);
    if (list) {
      // Create a copy to prevent concurrent modification issues
      const subscribers = Array.from(list);
      subscribers.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[EventBus] Error in callback for event "${event}":`, error);
        }
      });
    }
  }

  /**
   * Remove all registered listeners (primarily for hot reload and tests cleanup).
   */
  clearAll(): void {
    this.listeners.clear();
  }
}

export const eventBus = new EventBus();
export default eventBus;
